import { ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui-kit/card';

/** A label with its value; a missing value shows a dash. */
export const InfoRow = ({ label, value }: { label: ReactNode; value?: ReactNode }) => (
  <div className="flex flex-col gap-1 border-b py-2 text-sm last:border-0 sm:flex-row sm:items-baseline sm:gap-4">
    {/* Fixed label column (not `justify-between`) so the value sits right after it instead of
        being pushed to the far edge of a wide card, which read as an odd gap for short pairs. */}
    <span className="shrink-0 text-muted-foreground sm:w-48">{label}</span>
    {/* `||`, not `??`: date formatting yields '' (not null) for a missing date. */}
    <span className="min-w-0 flex-1 font-medium [overflow-wrap:anywhere]">{value || '-'}</span>
  </div>
);

export const InfoCard = ({ title, children }: { title: string; children: ReactNode }) => (
  <Card>
    <CardHeader>
      <CardTitle className="text-base">{title}</CardTitle>
    </CardHeader>
    {/* `pt-0 md:pt-0`: the header already ends in its own bottom padding, so stacking the
        content's default top padding on top of that left a gap under the title far bigger than
        the `py-2` the rows themselves use between each other. Needs both: `CardContent`'s own
        `md:p-6` also sets padding-top, and Tailwind places `md:` rules after plain ones, so a
        plain `pt-0` alone is overridden again at the `md` breakpoint. */}
    <CardContent className="pt-0 md:pt-0">{children}</CardContent>
  </Card>
);

/** Links are shown as such only when they are http(s) URLs (legacy `isValidUrl`). */
export const ExternalLink = ({ url }: { url?: string }) => {
  if (!url) return null;
  return /^https?:\/\//i.test(url) ? (
    <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary-600 underline">
      {url}
    </a>
  ) : (
    <span>{url}</span>
  );
};
